import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../services/ApiService';
import { installNativeBridgeHost, isRunningInBiltNativeShell } from '../utils/nativeShell';
import { setNoIndexMeta } from '../utils/metaTags';

/**
 * Exchanges a one-time native bootstrap token for a normal web session.
 * Loaded inside the native WebView only; not linked from public web UI.
 */
export default function NativeSessionBootstrapPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const exchangeStarted = useRef(false);

  useEffect(() => {
    setNoIndexMeta('Bilt:Spec');
  }, []);

  useEffect(() => {
    const cleanup = installNativeBridgeHost();
    return cleanup;
  }, []);

  useEffect(() => {
    const token = params.get('bootstrap_token') || params.get('t');
    const nextRaw = params.get('next') || '/';
    const next = nextRaw.startsWith('/') ? nextRaw : `/${nextRaw}`;

    if (!token) {
      navigate(next === '/native/session' ? '/' : next, { replace: true });
      return;
    }

    // Strict Mode remounts effects in React 18; the token is one-time-use.
    // A second exchange would fail and incorrectly bounce to /login.
    if (exchangeStarted.current) return;
    exchangeStarted.current = true;

    const finishAuthenticated = (destination: string) => {
      const dest = destination.startsWith('/') ? destination : `/${destination}`;
      navigate(dest === '/login' || dest === '/register' ? '/' : dest, { replace: true });
    };

    const finishFailed = () => {
      // Native already owns a valid JWT. Prefer continuing into the app over
      // flashing the login modal when exchange fails (consumed/expired token).
      if (apiService.isAuthenticated()) {
        finishAuthenticated(next);
        return;
      }
      if (isRunningInBiltNativeShell()) {
        navigate(next === '/login' ? '/' : next, { replace: true });
        return;
      }
      navigate('/login', { replace: true, state: { from: next } });
    };

    void (async () => {
      try {
        const response = await fetch(`${apiService.baseUrl}/native_web_session_exchange.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bootstrap_token: token }),
        });
        const body = await response.json();
        if (!response.ok || !body.success || !body.data?.token) {
          finishFailed();
          return;
        }
        apiService.saveAuthTokens({
          accessToken: body.data.token,
          refreshToken: body.data.refresh_token,
          expiresAt: body.data.expires_at,
          user: body.data.user,
        });
        sessionStorage.setItem('bilt_native_session_bootstrapped', '1');
        const destination =
          typeof body.data.next_path === 'string' && body.data.next_path.startsWith('/')
            ? body.data.next_path
            : next;
        finishAuthenticated(destination);
      } catch {
        finishFailed();
      }
    })();
  }, [navigate, params]);

  return (
    <div className="min-h-[40vh] flex items-center justify-center bg-[var(--bilt-bg-canvas)] text-[var(--bilt-text-muted)]">
      <p className="text-sm">Signing you in…</p>
    </div>
  );
}
